import React, { useState, useEffect } from "react";
import moment from "moment";
import {
    Modal,
    Box,
    Typography,
    TextField,
    Button,
    Stack,
    LinearProgress,
    Divider,
    CircularProgress,
    Tooltip,
    IconButton,
} from "@mui/material";
import { getCompanyGoalsService, getManagerGoalsService } from "../redux/service/commonService";
import { setCompanyLevelGoal, setManagerSpecificGoal, updateCompanyLevelGoal, updateManagerSpecificGoal } from "../redux/actions/commonActions";
import { useDispatch, useSelector } from "react-redux";
import showToast from "../utils/toast";
import { Edit } from "@mui/icons-material";
import { isSuperUser } from "../redux/selectors/userSelector";

const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 450,
    backgroundColor: 'white',
    boxShadow: 24,
    p: 4,
    borderRadius: 2,
};

const GoalModal = ({ open, onClose, managerId }) => {
    const dispatch = useDispatch();

    const [loading, setLoading] = useState(true);
    const [goalList, setGoalList] = useState([]);

    const [editCurrentGoal, setEditCurrentGoal] = useState(false);
    const [editNextGoal, setEditNextGoal] = useState(false);
    const [currentGoalInput, setCurrentGoalInput] = useState("");
    const [nextGoalInput, setNextGoalInput] = useState("");

    const activeManager = useSelector((state) => state.common.activeManager);

    const superUser = useSelector(isSuperUser);

    useEffect(() => {
        if (open) {
            setLoading(true);
            getGoals();
        }
    }, [open]);

    const getGoals = async () => {
        let goals = (managerId == null) ?
            await getCompanyGoalsService() :
            await getManagerGoalsService(managerId);

        setGoalList(goals.data);
        setLoading(false);
    }

    if (loading)
        return (
            <Modal open={open} onClose={onClose}>
                <Box sx={style} display="flex" justifyContent="center">
                    <CircularProgress />
                </Box>
            </Modal>
        );

    // ---------- Detect Dates via Moment ----------
    const today = moment();
    const currentYear = today.year();
    const currentMonth = today.month() + 1; // moment months are 0-based

    const nextMonthMoment = moment().add(1, "month");
    const nextYear = nextMonthMoment.year();
    const nextMonth = nextMonthMoment.month() + 1;

    const currentMonthItem = goalList.find(
        (g) => g.year === currentYear && g.month === currentMonth
    );

    const nextMonthItem = goalList.find(
        (g) => g.year === nextYear && g.month === nextMonth
    );

    // All others are previous months
    const previousMonths = goalList.filter(
        (g) =>
            !(g.year === currentYear && g.month === currentMonth) &&
            !(g.year === nextYear && g.month === nextMonth)
    );
    // ----------------------------------------------------

    const calculatePercentage = (achievedValue, goalValue) => {
        const percentage =
            goalValue && achievedValue
                ? Math.round((achievedValue / goalValue) * 100)
                : 0;
        return percentage;
    }

    const saveCurrentGoal = () => {
        if (editCurrentGoal) {
            handleUpdateGoal(currentMonthItem?.id, currentGoalInput);
        } else {
            handleSaveGoal(currentGoalInput, currentMonth, currentYear);
        }
    };

    const saveNextGoal = () => {
        if (editNextGoal) {
            handleUpdateGoal(nextMonthItem?.id, nextGoalInput);
        } else {
            handleSaveGoal(nextGoalInput, nextMonth, nextYear);
        }
    };

    const handleSaveGoal = async (goalValue, monthValue, yearValue) => {
        if (!goalValue) return showToast("Please set the goal value.", 'error');
        if (goalValue < 5) return showToast("Goal value should not be less than 5.", 'error')

        const aParams = {
            "goalValue": goalValue,
            "month": monthValue,
            "year": yearValue
        }

        if (managerId) {
            await dispatch(setManagerSpecificGoal(managerId, aParams));
        } else {
            await dispatch(setCompanyLevelGoal(aParams));
        }
        getGoals();
    }

    const handleUpdateGoal = async (goalId, goalValue) => {
        if (!goalValue) return showToast("Please set the goal value.", 'error');
        if (goalValue < 5) return showToast("Goal value should not be less than 5.", 'error')

        const aParams = {
            "goalValue": goalValue,
        }

        if (managerId) {
            await dispatch(updateManagerSpecificGoal(managerId, goalId, aParams));
        } else {
            await dispatch(updateCompanyLevelGoal(goalId, aParams));
        }
        setEditCurrentGoal(false);
        setEditNextGoal(false);
        getGoals();
    }

    const formatMonth = (year, month) =>
        moment(`${year}-${month}-01`).format("MMMM YYYY");

    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={style}>
                <Typography variant="h5" mb={2} fontWeight="bold">
                    {managerId == null ? "Company" : (activeManager ? activeManager.name : "Manager")}'s Monthly Goals
                </Typography>

                {/* -------- CURRENT MONTH -------- */}
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography style={{ fontSize: '14px' }}>
                        {formatMonth(currentYear, currentMonth)} — Current Month
                    </Typography>
                    <Typography style={{ fontSize: '14px' }}>
                        {calculatePercentage(currentMonthItem?.achievedValue, currentMonthItem?.goalValue)}% ({currentMonthItem?.achievedValue}/{currentMonthItem?.goalValue})
                    </Typography>
                </div>

                {(currentMonthItem?.goalValue || managerId == null) && !editCurrentGoal ? (
                    <div>
                        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginBottom: '8px' }}>
                            <Typography fontWeight={'bold'}>Goal: {currentMonthItem?.goalValue ? currentMonthItem?.goalValue : "Not set"}</Typography>
                            {superUser && managerId != null &&
                                <Tooltip style={{ marginLeft: '8px' }} title="Edit Current Month Goal">
                                    <IconButton
                                        size="small"
                                        sx={{
                                            backgroundColor: "#e3f2fd",
                                            color: '#1976d2',
                                            '&:hover': {
                                                backgroundColor: '#1976d2',
                                                color: '#e3f2fd'
                                            },
                                        }}
                                        onClick={() => {
                                            setCurrentGoalInput(currentMonthItem?.goalValue);
                                            setEditCurrentGoal(true);
                                        }}
                                    >
                                        <Edit fontSize="16px" />
                                    </IconButton>
                                </Tooltip>
                            }
                        </div>
                        {currentMonthItem?.goalValue &&
                            <LinearProgress
                                variant="determinate"
                                value={calculatePercentage(currentMonthItem?.achievedValue, currentMonthItem?.goalValue)}
                                sx={{ height: 8, borderRadius: 5 }}
                            />
                        }
                    </div>
                ) : (
                    <Stack direction="row" spacing={2} alignItems="center" mt={1} mb={2}>
                        <TextField
                            label="Set Current Month Goal"
                            type="number"
                            size="small"
                            value={currentGoalInput}
                            onChange={(e) => setCurrentGoalInput(e.target.value)}
                        />
                        <Button variant="outlined" onClick={saveCurrentGoal}>
                            {editCurrentGoal ? "Update" : "Save"}
                        </Button>
                    </Stack>
                )}

                <Divider sx={{ my: 2 }} />

                {/* -------- NEXT MONTH -------- */}
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography style={{ fontSize: '14px' }}>
                        {formatMonth(nextYear, nextMonth)} — Next Month
                    </Typography>
                    <Typography style={{ fontSize: '14px' }}>
                        {calculatePercentage(nextMonthItem?.achievedValue, nextMonthItem?.goalValue)}% ({nextMonthItem?.achievedValue}/{nextMonthItem?.goalValue})
                    </Typography>
                </div>

                {(nextMonthItem?.goalValue || managerId == null) && !editNextGoal ? (
                    <div>
                        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginBottom: '8px' }}>
                            <Typography>Goal: {nextMonthItem?.goalValue ? nextMonthItem?.goalValue : "Not set"}</Typography>
                            {superUser && managerId != null &&
                                <Tooltip style={{ marginLeft: '8px' }} title="Edit Next Month Goal">
                                    <IconButton
                                        size="small"
                                        sx={{
                                            backgroundColor: "#e3f2fd",
                                            color: '#1976d2',
                                            '&:hover': {
                                                backgroundColor: '#1976d2',
                                                color: '#e3f2fd'
                                            },
                                        }}
                                        onClick={() => {
                                            setNextGoalInput(nextMonthItem?.goalValue);
                                            setEditNextGoal(true);
                                        }}
                                    >
                                        <Edit fontSize="16px" />
                                    </IconButton>
                                </Tooltip>
                            }
                        </div>
                        {nextMonthItem?.goalValue &&
                            <LinearProgress
                                variant="determinate"
                                value={calculatePercentage(nextMonthItem?.achievedValue, nextMonthItem?.goalValue)}
                                sx={{ height: 8, borderRadius: 5 }}
                            />
                        }
                    </div>
                ) : (
                    <Stack direction="row" spacing={2} alignItems="center" mt={1} mb={2}>
                        <TextField
                            label="Set Next Month Goal"
                            type="number"
                            size="small"
                            value={nextGoalInput}
                            onChange={(e) => setNextGoalInput(e.target.value)}
                        />
                        <Button variant="outlined" onClick={saveNextGoal}>
                            {editNextGoal ? "Update" : "Save"}
                        </Button>
                    </Stack>
                )}

                <Divider sx={{ my: 2 }} />

                {/* -------- PREVIOUS MONTHS -------- */}
                <Typography variant="h6" mb={2}>
                    Previous Months
                </Typography>

                <Stack spacing={2} maxHeight="220px" sx={{ overflowY: "auto" }}>
                    {previousMonths.map((item, idx) => {
                        const percentage =
                            item.goalValue && item.achievedValue
                                ? Math.round((item.achievedValue / item.goalValue) * 100)
                                : 0;

                        return (
                            <Box key={idx}>
                                <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                >
                                    <Typography style={{ fontSize: '14px' }}>
                                        {formatMonth(item.year, item.month)}
                                    </Typography>

                                    {item.goalValue ? (
                                        <Typography style={{ fontSize: '14px' }}>
                                            {percentage}% ({item.achievedValue}/{item.goalValue})
                                        </Typography>
                                    ) : (
                                        <Typography>No Goal Set</Typography>
                                    )}
                                </Stack>

                                {item.goalValue && (
                                    <LinearProgress
                                        variant="determinate"
                                        value={percentage}
                                        sx={{ height: 8, borderRadius: 5 }}
                                    />
                                )}
                            </Box>
                        );
                    })}
                </Stack>

                {/* -------- FOOTER -------- */}
                <Stack direction="row" justifyContent="flex-end" mt={3}>
                    <Button onClick={onClose}>Close</Button>
                </Stack>
            </Box>
        </Modal >
    );
}

export default GoalModal;
